import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import {
  AiAgentSession,
  ContactInteractionDirection,
  ContactInteractionPhoneCallDetail,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  CallAnalysisDetails,
  CallAnalysisDetailsTabKey,
} from 'src/components/pages/conversations/components/contact-interactions/items/phone-call-analysis/components/call-analysis-details';
import { InteractionDetailsBadge } from 'src/components/pages/conversations/components/contact-interactions/items/phone-call-analysis/components/interaction-details-badge';
import { useVisible } from 'src/hooks/use-visible';
import { ContactInteractionExtraDetails } from './interaction-details';
import { InteractionDetailsDrawer } from './interaction-details-drawer';
import {
  SummaryBadge,
  AiAgentsGoalsBadge,
  AiAgentsActionsBadge,
  AiAgentsInvolvedBadge,
  InteractionTruncatedTextDetail,
  getInteractionDetailBrief,
  MoreDetailsOption,
} from './shared';

const AnalysisInProgressMessageDivider = styled.div`
  background-image: linear-gradient(
    to right,
    rgba(152, 169, 188, 0),
    ${({ theme }) => theme.colors.flint}
  );
  display: none;
  height: 1px;
  opacity: 0.5;
  width: 60px;

  ${up('md')} {
    display: block;
  }
`;

const AnalysisInProgressMessageContainer = styled(FlexRow)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.alabaster};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  justify-content: center;
  margin-bottom: 10px;
  padding: 14px 27px;

  ${up('md')} {
    padding: 14px 10px;

    ${AnalysisInProgressMessageDivider} {
      &:last-child {
        transform: rotate(180deg);
      }
    }
  }
`;

const AnalysisInProgressMessageText = styled.div`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: -0.24px;
  line-height: 14px;

  ${up('md')} {
    margin: 0 10px;
  }
`;

const InProgressMessageStrongText = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.medium};
`;

const AnalysisInProgressMessage = () => {
  return (
    <AnalysisInProgressMessageContainer data-lgg-id="analysis-in-progress-message">
      <AnalysisInProgressMessageDivider />
      <AnalysisInProgressMessageText>
        <Trans
          i18nKey="conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.summaryInProgress"
          components={{ strong: <InProgressMessageStrongText /> }}
        />
      </AnalysisInProgressMessageText>
      <AnalysisInProgressMessageDivider />
    </AnalysisInProgressMessageContainer>
  );
};

type PhoneCallAnalysisProps = {
  details: ContactInteractionPhoneCallDetail;
  recordingSrc: string;
  direction: ContactInteractionDirection;
  aiAgentSessions?: AiAgentSession[];
};

export const PhoneCallAnalysis = ({
  details,
  recordingSrc,
  direction,
  aiAgentSessions = [],
}: PhoneCallAnalysisProps) => {
  const { t } = useTranslation(['conversations', 'common']);
  const callAnalysisDetailsVisibilityHandler = useVisible();
  const {
    brief: sentimentAnalysisBrief,
    sentimentAnalysis,
    summary: sentimentAnalysisSummary,
    transcription,
  } = details;
  const [detailsSelectedTabKey, setDetailsSelectedTabKey] =
    useState<CallAnalysisDetailsTabKey>('overview');

  const openDetailsAtOverviewTab = useCallback(() => {
    setDetailsSelectedTabKey('overview');
    callAnalysisDetailsVisibilityHandler.show();
  }, [callAnalysisDetailsVisibilityHandler]);

  const aiAgentSummary = useMemo(() => {
    return aiAgentSessions[aiAgentSessions.length - 1]?.summary ?? null;
  }, [aiAgentSessions]);

  const isSentimentAnalysisSummaryUnavailable =
    !sentimentAnalysisSummary ||
    sentimentAnalysisSummary.__typename === 'ContactInteractionSummaryDisabled';

  const summary = useMemo(() => {
    if (isSentimentAnalysisSummaryUnavailable) {
      return aiAgentSummary || sentimentAnalysisSummary;
    }

    return sentimentAnalysisSummary;
  }, [aiAgentSummary, isSentimentAnalysisSummaryUnavailable, sentimentAnalysisSummary]);

  const hasAnalysisToShow = useMemo(() => {
    return [sentimentAnalysisBrief, sentimentAnalysis, summary, transcription].some(
      (operation) => {
        const isOperationDoneOrPending = match(operation)
          .with(
            P.union(
              { __typename: 'SentimentAnalysisDisabled' },
              { __typename: 'ContactInteractionBriefDisabled' },
              { __typename: 'ContactInteractionSummaryDisabled' },
              { __typename: 'ContactInteractionTranscriptionDisabled' },
            ),
            () => false,
          )
          .otherwise(() => true);

        return operation && isOperationDoneOrPending;
      },
    );
  }, [sentimentAnalysis, sentimentAnalysisBrief, summary, transcription]);

  const hasFinishedAnalysis = useMemo(() => {
    return [sentimentAnalysisBrief, sentimentAnalysis, summary, transcription].every(
      (operation) => {
        const isOperationDone = match(operation)
          .with({ __typename: 'AsyncStatusPending' }, () => false)
          .otherwise(() => true);

        return isOperationDone === true;
      },
    );
  }, [sentimentAnalysisBrief, sentimentAnalysis, summary, transcription]);

  const briefText = useMemo(() => {
    const aiAgentBrief = getInteractionDetailBrief(aiAgentSummary ?? undefined);

    if (!sentimentAnalysisBrief) return aiAgentBrief;

    try {
      return match(sentimentAnalysisBrief)
        .with({ __typename: 'ContactInteractionBrief' }, (brief) => brief.text)
        .with({ __typename: 'ContactInteractionBriefDisabled' }, () => aiAgentBrief)
        .with({ __typename: 'AsyncStatusPending' }, () => null)
        .exhaustive();
    } catch (_error) {
      return null;
    }
  }, [aiAgentSummary, sentimentAnalysisBrief]);

  const sentimentAnalysisBadges = useMemo(() => {
    if (!sentimentAnalysis) return null;

    try {
      return match(sentimentAnalysis)
        .with({ __typename: 'AsyncStatusPending' }, () => (
          <InteractionDetailsBadge
            icon="neutralEmoji"
            title={t(
              'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.sentiments',
            )}
            type="neutral"
            inProgress
            data-lgg-id="sentiment-analysis-pending-badge"
            onClick={openDetailsAtOverviewTab}
          />
        ))
        .with(
          { __typename: 'ContactInteractionSentimentAnalysis' },
          ({ totalPositives, totalNegatives }) => {
            const sentimentsFound = totalPositives > 0 || totalNegatives > 0;

            if (sentimentsFound) {
              return (
                <>
                  {totalPositives ? (
                    <InteractionDetailsBadge
                      icon="happyEmoji"
                      title={t(
                        'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.positiveSentiment',
                        { count: totalPositives },
                      )}
                      type="success"
                      data-lgg-id="sentiment-analysis-positives-badge"
                      onClick={openDetailsAtOverviewTab}
                    />
                  ) : null}
                  {totalNegatives ? (
                    <InteractionDetailsBadge
                      icon="sadEmoji"
                      title={t(
                        'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.negativeSentiment',
                        { count: totalNegatives },
                      )}
                      type="negative"
                      data-lgg-id="sentiment-analysis-negatives-badge"
                      onClick={openDetailsAtOverviewTab}
                    />
                  ) : null}
                </>
              );
            }

            if (
              transcription?.__typename === 'ContactInteractionTranscription' &&
              transcription.phrases.edges.length > 0
            ) {
              return (
                <InteractionDetailsBadge
                  icon="neutralEmoji"
                  title={t(
                    'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.neutralSentiment',
                  )}
                  type="neutral"
                  data-lgg-id="sentiment-analysis-neutral-badge"
                  onClick={openDetailsAtOverviewTab}
                />
              );
            }

            return null;
          },
        )
        .with({ __typename: 'SentimentAnalysisDisabled' }, () => null)
        .exhaustive();
    } catch (_error) {
      return null;
    }
  }, [openDetailsAtOverviewTab, sentimentAnalysis, t, transcription]);

  const transcriptBadge = useMemo(() => {
    if (!transcription) return null;

    // Keep ind mind that ContactInteractionPhoneCallTranscriptionUnion may change
    // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
    return match(transcription)
      .with({ __typename: 'ContactInteractionTranscriptionDisabled' }, () => null)
      .with(
        P.union(
          { __typename: 'AsyncStatusPending' },
          { __typename: 'ContactInteractionTranscription' },
        ),
        (transcription) => {
          const isInProgress = transcription.asyncStatus === 'PENDING';
          const hasTranscript =
            transcription.__typename === 'ContactInteractionTranscription' &&
            transcription.phrases.edges.length > 0;

          const badgeBuilder = (isInProgress: boolean) => {
            return (
              <InteractionDetailsBadge
                icon="transcript"
                title={t(
                  'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.transcript',
                )}
                type="neutral"
                inProgress={isInProgress}
                data-lgg-id="call-transcription-badge"
                onClick={() => {
                  if (!isInProgress) {
                    setDetailsSelectedTabKey('transcript');
                    callAnalysisDetailsVisibilityHandler.show();
                  }
                }}
              />
            );
          };

          if (isInProgress) {
            return badgeBuilder(isInProgress);
          }

          if (!hasTranscript) {
            return null;
          }

          return badgeBuilder(false);
        },
      )
      .exhaustive();
  }, [callAnalysisDetailsVisibilityHandler, t, transcription]);

  const summaryBadge = useMemo(() => {
    if (isSentimentAnalysisSummaryUnavailable) {
      if (aiAgentSummary) {
        return (
          <SummaryBadge
            onClick={() => {
              openDetailsAtOverviewTab();
            }}
          />
        );
      }

      return null;
    }

    return match(sentimentAnalysisSummary)
      .with(
        P.union(
          { __typename: 'AsyncStatusPending' },
          { __typename: 'ContactInteractionSummary' },
        ),
        (summary) => {
          const isInProgress = summary.asyncStatus === 'PENDING';

          return (
            <SummaryBadge
              isInProgress={isInProgress}
              onClick={() => {
                if (!isInProgress) {
                  openDetailsAtOverviewTab();
                }
              }}
            />
          );
        },
      )
      .otherwise(() => null);
  }, [
    aiAgentSummary,
    isSentimentAnalysisSummaryUnavailable,
    openDetailsAtOverviewTab,
    sentimentAnalysisSummary,
  ]);

  const summaryText = useMemo(() => {
    if (isSentimentAnalysisSummaryUnavailable) {
      return aiAgentSummary;
    }

    return match(sentimentAnalysisSummary)
      .with({ __typename: 'ContactInteractionSummary' }, (summary) => summary.text)
      .otherwise(() => null);
  }, [aiAgentSummary, isSentimentAnalysisSummaryUnavailable, sentimentAnalysisSummary]);

  const goalsBadge = useMemo(() => {
    const goals = aiAgentSessions.flatMap((session) => session.goals);
    if (!goals.length) return null;

    return <AiAgentsGoalsBadge goals={goals} onClick={openDetailsAtOverviewTab} />;
  }, [aiAgentSessions, openDetailsAtOverviewTab]);

  const actionsBadge = useMemo(() => {
    const actionsCount = aiAgentSessions.flatMap((session) => session.toolCalls).length;

    return (
      <AiAgentsActionsBadge
        actionsCount={actionsCount}
        onClick={openDetailsAtOverviewTab}
      />
    );
  }, [aiAgentSessions, openDetailsAtOverviewTab]);

  const aiAgentsInvolvedBadge = useMemo(() => {
    if (!aiAgentSessions.length) return null;

    const aiAgentsIds = [
      ...new Set(aiAgentSessions.map((session) => session.aiAgent?.id)),
    ];

    return (
      <AiAgentsInvolvedBadge
        agentsCount={aiAgentsIds.length}
        onClick={openDetailsAtOverviewTab}
      />
    );
  }, [aiAgentSessions, openDetailsAtOverviewTab]);

  const callAnalysisDetails = useMemo(
    () => (
      <CallAnalysisDetails
        summaryText={summaryText ?? undefined}
        sentimentAnalysis={sentimentAnalysis ?? undefined}
        transcription={transcription ?? undefined}
        recordingSrc={recordingSrc}
        defaultSelectedTab={detailsSelectedTabKey}
        aiAgentSessions={aiAgentSessions}
      />
    ),
    [
      summaryText,
      sentimentAnalysis,
      transcription,
      recordingSrc,
      detailsSelectedTabKey,
      aiAgentSessions,
    ],
  );

  if (!hasAnalysisToShow) return null;

  return (
    <ContactInteractionExtraDetails
      title={t('conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.title')}
      testId="phone-call-analysis"
      topContent={
        hasFinishedAnalysis ? (
          briefText ? (
            <InteractionTruncatedTextDetail
              data-lgg-id="call-analysis-brief"
              direction={direction}
            >
              {briefText}
            </InteractionTruncatedTextDetail>
          ) : null
        ) : (
          <AnalysisInProgressMessage />
        )
      }
      mainContent={
        <>
          {sentimentAnalysisBadges}
          {transcriptBadge}
          {summaryBadge}
          {goalsBadge}
          {actionsBadge}
          {aiAgentsInvolvedBadge}
          <InteractionDetailsDrawer
            testId="call-details-drawer"
            visibilityHandler={callAnalysisDetailsVisibilityHandler}
            title={t(
              'conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.moreDetailsDrawer.title',
            )}
            content={callAnalysisDetails}
          />
        </>
      }
      bottomContent={
        hasFinishedAnalysis ? (
          <MoreDetailsOption
            onClick={callAnalysisDetailsVisibilityHandler.show}
            data-lgg-id="call-details-link-button"
          >
            {t('common:seeMoreDetails')}
          </MoreDetailsOption>
        ) : null
      }
    />
  );
};
